import { FACE_PILE_LIMIT } from './design/facePile';
import React, { useState, useEffect, useRef } from 'react';
import axios from './lib/adminApi';
import FeedbackInbox from './components/FeedbackInbox';
import config from './config';
import { dateTime } from './admin/api';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';

import { Badge } from './components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './components/ui/dialog';
import { Input } from './components/ui/input';
import { Select } from './components/ui/select';
import { Label } from './components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './components/ui/table';
import CytoscapeComponent from 'react-cytoscapejs';
import type { Core as CytoscapeCore, EventObject } from 'cytoscape';
import cytoscape from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';
import { formatMoney, normalizeCurrency, type SupportedCurrency } from './lib/currency';

interface User {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber: string;
  username?: string;
  venmoUsername?: string;
  profilePictureUrl?: string;
  venmoProfilePictureOriginalUrl?: string;
  userType?: string;
  status?: string; // Account status: pending_setup, pending_phone_verify, pending_venmo, active
  createdAt: string;
  receiptBillLimit?: number;
  receiptBillsUsed?: number;
  quotaPeriodStart?: string;
  isDeleted?: boolean;
  appleUserId?: string;
  authProvider?: string;
  admin?: boolean;
}

interface TripMember {
  id: string;
  name?: string;
  type?: string;
  role?: string;
  profilePictureUrl?: string;
  venmoProfilePictureUrl?: string;
  [key: string]: any;
}

interface Trip {
  id: string;
  name?: string;
  creatorId: string;
  shareId?: string;
  status?: string;
  members?: TripMember[];
  memberIds?: string[];
  createdAt?: string;
  updatedAt?: string;
  isTestData?: boolean;
  [key: string]: any;
}

interface RecurringBillTemplate {
  id: string;
  userId: string;
  creator?: string;
  title: string;
  total: number;
  currency?: string;
  participants?: Array<{
    id: string;
    name?: string;
    type?: string;
    profilePictureUrl?: string;
    venmoProfilePictureUrl?: string;
  }>;
  participantShares?: Array<{ participantId: string; total: number }>;
  paidBy?: string;
  schedule?: { cadence?: string; anchorDay?: number };
  repeatCount?: number | null;
  occurrencesCreated?: number;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  scheduleStatus?: 'active' | 'paused' | 'completed';
  createdAt?: string;
  isTestData?: boolean;
  [key: string]: any;
}

export type LegacySection =
  | 'trips'
  | 'recurring'
  | 'notifications'
  | 'social'
  | 'sandbox'
  | 'features'
  | 'feedback';

interface Notification {
  id: string;
  createdAt: string;
  channel: string;
  status: 'sent' | 'failed' | 'suppressed';
  type: string;
  userId: string;
  recipientName?: string;
  title: string;
  body: string;
  billId?: string;
  billName?: string;
  tripId?: string;
  pairKey?: string;
  amount?: number;
  error?: string;
  ticketId?: string;
  isTestData?: boolean;
  // Expo receipt verdict: delivered to Apple/Google, bounced, or receipt
  // expired unchecked. Absent = still awaiting the receipt.
  deliveryStatus?: 'delivered' | 'failed' | 'unknown';
  deliveryError?: string;
}

interface FeatureFlag {
  flagId: string;
  key: string;
  name: string;
  description: string;
  enabledForAll: boolean;
  allowedUsers: string[];
  createdAt: string;
  updatedAt: string;
}

interface ParsedBillItemAddon {
  name: string;
  price: number;
}

interface ParsedBillItem {
  id: string;
  name: string;
  description?: string;
  price: number;
  amount: number;
  quantity: number;
  addons?: ParsedBillItemAddon[];
}

interface ParsedBillResult {
  title: string;
  businessName: string;
  items: ParsedBillItem[];
  subtotal: number;
  tax: number;
  tip: number;
  discounts: Record<string, number>;
  surcharges: Record<string, number>;
  total: number;
  currency?: SupportedCurrency | string;
}

interface SandboxProcessedImage {
  index: number;
  sourceUrl: string;
  mediaType: string;
  originalBytes: number;
  processedBytes: number;
  convertedFromHeic: boolean;
  wasCompressed: boolean;
  dataUrl?: string;
}

interface SandboxDetails {
  prompts?: string[];
  rawResponses?: string[];
  processedImages?: SandboxProcessedImage[];
  callMetrics?: Array<{
    model?: string;
    usage?: {
      input_tokens?: number;
      output_tokens?: number;
    } | null;
    estimatedCostUsd?: number | null;
  }>;
}

interface SandboxParseResponse {
  success: boolean;
  parsedBill: ParsedBillResult;
  receiptData?: {
    confidence?: number;
    bill_completeness_score?: number;
    billCompletenessScore?: number;
    confidence_explanation?: string;
    confidenceExplanation?: string;
  };
  sandboxDetails?: SandboxDetails | null;
  model?: string;
  imageCount: number;
  claudeDurationMs?: number;
}

const AdminLegacyPanels = ({
  section,
  refreshKey,
  notificationUser,
  onUser,
  onBill,
}: {
  section: LegacySection;
  refreshKey: number;
  notificationUser?: { id: string; name: string };
  onUser: (id: string) => void;
  onBill: (id: string) => void;
}) => {
  const activeTab = section;
  const [panelError, setPanelError] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [recurringBills, setRecurringBills] = useState<RecurringBillTemplate[]>([]);
  const [sweepResult, setSweepResult] = useState<string | null>(null);
  const [sweepRunning, setSweepRunning] = useState(false);
  const notificationRequest = useRef(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationSearch, setNotificationSearch] = useState('');
  // When set, the notifications tab shows one recipient's history (via the
  // userId GSI) instead of the global feed.
  const [notificationUserFilter, setNotificationUserFilter] = useState<{
    id: string;
    name: string;
  } | null>(notificationUser || null);
  // Server-side column filters + cursor pagination for the notifications tab.
  const [notifStatusFilter, setNotifStatusFilter] = useState('');
  const [notifTypeFilter, setNotifTypeFilter] = useState('');
  const [notifDeliveryFilter, setNotifDeliveryFilter] = useState('');
  const [notifNextCursor, setNotifNextCursor] = useState<string | null>(null);
  const [notifHasMore, setNotifHasMore] = useState(false);
  const [notifLoadingMore, setNotifLoadingMore] = useState(false);
  // Row expanded to its full, untruncated detail (id, payload, delivery info).
  const [expandedNotificationId, setExpandedNotificationId] = useState<string | null>(null);
  const [notificationsTableMissing, setNotificationsTableMissing] = useState(false);
  const [tabLoading, setTabLoading] = useState<Record<string, boolean>>({ [section]: true });
  const sectionRequest = useRef(0);
  const [showTestData, setShowTestData] = useState(false);
  const [socialGraph, setSocialGraph] = useState<{ nodes: any[]; links: any[] }>({
    nodes: [],
    links: [],
  });
  const [sandboxFiles, setSandboxFiles] = useState<File[]>([]);
  const [sandboxParsing, setSandboxParsing] = useState(false);
  const [sandboxError, setSandboxError] = useState('');
  const [sandboxErrorDetails, setSandboxErrorDetails] = useState('');
  const [sandboxResult, setSandboxResult] = useState<SandboxParseResponse | null>(null);
  const [sandboxModel, setSandboxModel] = useState('claude-sonnet-5');
  const [sandboxPreviewImage, setSandboxPreviewImage] = useState<{
    src: string;
    title: string;
  } | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [featureFlagModalOpen, setFeatureFlagModalOpen] = useState(false);
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [flagForm, setFlagForm] = useState({
    key: '',
    name: '',
    description: '',
    enabledForAll: false,
    allowedUsers: [] as string[],
  });
  const [flagUserSearch, setFlagUserSearch] = useState('');
  const [flagConfirmDialog, setFlagConfirmDialog] = useState<{
    type: 'delete' | 'enableAll';
    flagId?: string;
    flagKey?: string;
  } | null>(null);
  const cyRef = useRef<CytoscapeCore | null>(null);
  const coseRegisteredRef = useRef(false);

  useEffect(() => {
    setPanelError('');
    void loadTabData(section);
    // Keep one loader for the current section and filter combination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    section,
    refreshKey,
    showTestData,
    notificationUserFilter,
    notifStatusFilter,
    notifTypeFilter,
    notifDeliveryFilter,
  ]);
  useEffect(() => {
    setNotificationUserFilter((previous) =>
      previous?.id === notificationUser?.id && previous?.name === notificationUser?.name
        ? previous
        : notificationUser
          ? { id: notificationUser.id, name: notificationUser.name }
          : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationUser?.id, notificationUser?.name]);
  const feedbackRefreshKey = refreshKey;
  useEffect(() => {
    if (!coseRegisteredRef.current) {
      cytoscape.use(coseBilkent);
      coseRegisteredRef.current = true;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, []);

  const loadTabData = async (tab: typeof activeTab) => {
    const request = ++sectionRequest.current;
    setTabLoading((prev) => ({ ...prev, [tab]: true }));
    try {
      if (tab === 'trips') {
        await Promise.all([loadTrips(), loadUsers()]);
      } else if (tab === 'recurring') {
        await Promise.all([loadRecurringBills(), loadUsers()]);
      } else if (tab === 'notifications') {
        await loadNotifications();
      } else if (tab === 'social') {
        await loadSocialGraph();
      } else if (tab === 'feedback') {
        // Feedback has its own paginated loader.
      } else if (tab === 'features') {
        await Promise.all([loadFeatureFlags(), loadUsers()]);
      }
    } catch (error) {
      setPanelError('Couldn’t load this section. Use Refresh to try again.');
      console.error('Error loading admin data:', error);
    } finally {
      if (request === sectionRequest.current) setTabLoading((prev) => ({ ...prev, [tab]: false }));
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/admin-dashboard/users`);
      if (response.data.success) {
        setUsers(response.data.users || []);
      }
    } catch (error: any) {
      setPanelError('Couldn’t load this section. Use Refresh to try again.');
      console.error('Error loading users:', error);
    }
  };

  const loadTrips = async (includeTestData: boolean = showTestData) => {
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/admin-dashboard/trips?includeTestData=${includeTestData}`
      );
      if (response.data.success) {
        setTrips(response.data.trips || []);
      }
    } catch (error) {
      setPanelError('Couldn’t load this section. Use Refresh to try again.');
      console.error('Error loading trips:', error);
    }
  };

  const loadRecurringBills = async (includeTestData: boolean = showTestData) => {
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/admin-dashboard/recurring-bills?includeTestData=${includeTestData}`
      );
      if (response.data.success) {
        setRecurringBills(response.data.recurringBills || []);
      }
    } catch (error) {
      setPanelError('Couldn’t load this section. Use Refresh to try again.');
      console.error('Error loading recurring bills:', error);
    }
  };

  const handleRecurringStatus = async (rec: RecurringBillTemplate) => {
    const next = rec.scheduleStatus === 'paused' ? 'active' : 'paused';
    try {
      await axios.put(`${config.apiBaseUrl}/admin-dashboard/recurring-bills/${rec.id}/status`, {
        scheduleStatus: next,
      });
      await loadRecurringBills();
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to update recurring bill');
    }
  };

  const handleRecurringDelete = async (rec: RecurringBillTemplate) => {
    if (!window.confirm(`Delete recurring bill "${rec.title}"? Generated bills stay intact.`))
      return;
    try {
      await axios.delete(`${config.apiBaseUrl}/admin-dashboard/recurring-bills/${rec.id}`);
      await loadRecurringBills();
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to delete recurring bill');
    }
  };

  const handleRunSweep = async () => {
    setSweepRunning(true);
    setSweepResult(null);
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}/admin-dashboard/recurring-bills/sweep`
      );
      const { due, generated, lostRace, failed, skipped } = response.data || {};
      setSweepResult(
        skipped
          ? 'Sweep already in progress — skipped.'
          : `Sweep done: ${due ?? 0} due, ${generated ?? 0} generated, ${lostRace ?? 0} lost race, ${failed ?? 0} failed.`
      );
      await loadRecurringBills();
    } catch (error: any) {
      setSweepResult(error?.response?.data?.error || 'Sweep failed');
    } finally {
      setSweepRunning(false);
    }
  };

  const buildNotificationsQuery = (includeTestData: boolean, cursor?: string | null) => {
    const params = new URLSearchParams();
    params.set('limit', '50');
    params.set('includeTestData', String(includeTestData));
    if (notificationUserFilter) params.set('userId', notificationUserFilter.id);
    if (notifStatusFilter) params.set('status', notifStatusFilter);
    if (notifTypeFilter) params.set('type', notifTypeFilter);
    if (notifDeliveryFilter) params.set('deliveryStatus', notifDeliveryFilter);
    if (cursor) params.set('cursor', cursor);
    return params.toString();
  };

  const loadNotifications = async (includeTestData: boolean = showTestData) => {
    const request = ++notificationRequest.current;
    setNotifications([]);
    setNotifNextCursor(null);
    setNotifHasMore(false);
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/admin-dashboard/notifications?${buildNotificationsQuery(includeTestData)}`
      );
      if (request === notificationRequest.current && response.data.success) {
        setNotifications(response.data.notifications || []);
        setNotifHasMore(!!response.data.hasMore);
        setNotifNextCursor(response.data.nextCursor || null);
        setNotificationsTableMissing(!!response.data.tableMissing);
      }
    } catch (error) {
      if (request === notificationRequest.current)
        setPanelError('Couldn’t load this section. Use Refresh to try again.');
      console.error('Error loading notifications:', error);
    }
  };

  const loadMoreNotifications = async () => {
    if (!notifNextCursor || notifLoadingMore) return;
    const request = notificationRequest.current;
    setNotifLoadingMore(true);
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/admin-dashboard/notifications?${buildNotificationsQuery(showTestData, notifNextCursor)}`
      );
      if (request === notificationRequest.current && response.data.success) {
        setNotifications((prev) =>
          Array.from(
            new Map(
              [...prev, ...(response.data.notifications || [])].map((n) => [n.id, n])
            ).values()
          )
        );
        setNotifHasMore(!!response.data.hasMore);
        setNotifNextCursor(response.data.nextCursor || null);
      }
    } catch (error) {
      if (request === notificationRequest.current)
        setPanelError('Couldn’t load this section. Use Refresh to try again.');
      console.error('Error loading more notifications:', error);
    } finally {
      setNotifLoadingMore(false);
    }
  };

  const loadSocialGraph = async (forceRefresh: boolean = false) => {
    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/admin-dashboard/social-graph${forceRefresh ? '?refresh=true' : ''}`
      );
      if (response.data.success) {
        setSocialGraph({
          nodes: response.data.nodes || [],
          links: response.data.links || [],
        });
      }
    } catch (error) {
      setPanelError('Couldn’t load this section. Use Refresh to try again.');
      console.error('Error loading social graph:', error);
    }
  };

  const loadFeatureFlags = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/admin-dashboard/features`);
      if (response.data.success) {
        setFeatureFlags(response.data.flags || []);
      }
    } catch (error) {
      setPanelError('Couldn’t load this section. Use Refresh to try again.');
      console.error('Error loading feature flags:', error);
    }
  };

  const handleCreateFlag = async () => {
    try {
      const payload = {
        key: flagForm.key,
        name: flagForm.name,
        description: flagForm.description,
        enabledForAll: flagForm.enabledForAll,
        allowedUsers: flagForm.allowedUsers,
      };
      await axios.post(`${config.apiBaseUrl}/admin-dashboard/features`, payload);
      setFeatureFlagModalOpen(false);
      setFlagForm({ key: '', name: '', description: '', enabledForAll: false, allowedUsers: [] });
      setFlagUserSearch('');
      await loadFeatureFlags();
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to create flag');
    }
  };

  const handleUpdateFlag = async () => {
    if (!editingFlag) return;
    // If enabling for all and it wasn't before, require confirmation first
    if (flagForm.enabledForAll && !editingFlag.enabledForAll) {
      setFlagConfirmDialog({
        type: 'enableAll',
        flagId: editingFlag.flagId,
        flagKey: editingFlag.key,
      });
      return;
    }
    await executeUpdateFlag();
  };

  const executeUpdateFlag = async () => {
    if (!editingFlag) return;
    try {
      const payload = {
        key: flagForm.key,
        name: flagForm.name,
        description: flagForm.description,
        enabledForAll: flagForm.enabledForAll,
        allowedUsers: flagForm.allowedUsers,
      };
      await axios.put(
        `${config.apiBaseUrl}/admin-dashboard/features/${editingFlag.flagId}`,
        payload
      );
      setFeatureFlagModalOpen(false);
      setEditingFlag(null);
      setFlagForm({ key: '', name: '', description: '', enabledForAll: false, allowedUsers: [] });
      setFlagUserSearch('');
      await loadFeatureFlags();
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to update flag');
    }
  };

  const handleDeleteFlag = (flagId: string, flagKey: string) => {
    setFlagConfirmDialog({ type: 'delete', flagId, flagKey });
  };

  const executeDeleteFlag = async () => {
    if (!flagConfirmDialog?.flagId) return;
    try {
      await axios.delete(
        `${config.apiBaseUrl}/admin-dashboard/features/${flagConfirmDialog.flagId}`
      );
      setFlagConfirmDialog(null);
      await loadFeatureFlags();
    } catch (error) {
      alert('Failed to delete flag');
    }
  };

  const handleSandboxFilesChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSandboxFiles(files.slice(0, 5));
    setSandboxError('');
  };

  const convertImageFileToJpeg = async (file: File): Promise<File> => {
    if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
      return file;
    }

    const objectUrl = URL.createObjectURL(file);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Unable to decode image: ${file.name}`));
        img.src = objectUrl;
      });

      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;

      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Unable to initialize canvas for image conversion');
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      const jpegBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error(`Failed to convert image to JPEG: ${file.name}`));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          0.7
        );
      });

      const baseName = file.name.replace(/\.[^.]+$/, '');
      return new File([jpegBlob], `${baseName}.jpg`, { type: 'image/jpeg' });
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  const handleSandboxParse = async () => {
    if (sandboxFiles.length === 0 || sandboxParsing) {
      return;
    }

    setSandboxParsing(true);
    setSandboxError('');
    setSandboxErrorDetails('');
    setSandboxResult(null);

    try {
      const jpegFiles = await Promise.all(sandboxFiles.map((file) => convertImageFileToJpeg(file)));
      const formData = new FormData();
      jpegFiles.forEach((file) => formData.append('receipts', file));
      formData.append('model', sandboxModel);

      const response = await axios.post<SandboxParseResponse>(
        `${config.apiBaseUrl}/admin-dashboard/receipt-sandbox/parse`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000,
        }
      );

      setSandboxResult(response.data);
    } catch (error: any) {
      const details = error?.response?.data?.details;
      const detailsText = details
        ? typeof details === 'string'
          ? details
          : JSON.stringify(details, null, 2)
        : '';
      setSandboxError(
        error?.response?.data?.error ||
          error?.message ||
          'Failed to parse receipt images. Please try clearer photos.'
      );
      setSandboxErrorDetails(detailsText);
    } finally {
      setSandboxParsing(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    return `${value.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
  };

  const getNotificationStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'suppressed':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      default:
        return 'bg-muted text-foreground border-border';
    }
  };

  const getTripShareUrl = (shareId: string): string => {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/t/${shareId}`;
    }
    return `https://vibesplit.xyz/t/${shareId}`;
  };

  const formatClaudeConfidence = (confidence?: number | null): string => {
    if (confidence == null || !Number.isFinite(Number(confidence))) {
      return 'N/A';
    }
    const numeric = Number(confidence);
    const pct = numeric <= 1 ? numeric * 100 : numeric;
    return `${pct.toFixed(2)}%`;
  };

  const formatBillCompletenessScore = (value?: number | null): string => {
    if (value == null || !Number.isFinite(Number(value))) {
      return 'N/A';
    }
    const numeric = Number(value);
    const pct = numeric <= 1 ? numeric * 100 : numeric;
    return `${pct.toFixed(2)}%`;
  };

  const getBillCompletenessScore = (result: SandboxParseResponse | null): number | null => {
    if (!result?.receiptData) return null;
    const value =
      result.receiptData.bill_completeness_score ?? result.receiptData.billCompletenessScore;
    return value == null ? null : Number(value);
  };

  const getConfidenceExplanation = (result: SandboxParseResponse | null): string => {
    if (!result?.receiptData) return 'No explanation provided.';
    return (
      result.receiptData.confidence_explanation ||
      result.receiptData.confidenceExplanation ||
      'No explanation provided.'
    );
  };

  const formatBillAmount = (value: number, currency?: string | null): string =>
    formatMoney(Number(value || 0), currency || 'USD');

  const renderSocialGraph = () => {
    const elements = [
      ...socialGraph.nodes.map((node) => {
        const fullName = node.label || 'Guest';
        const name = fullName.split(' ')[0] || fullName;
        const initials = name
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((part: string) => part[0]?.toUpperCase())
          .join('');
        const hasProfile = Boolean(node.profilePictureUrl);
        const initialsSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="100%" height="100%" fill="#e2e8f0"/><text x="50%" y="54%" text-anchor="middle" font-size="72" font-family="Arial, sans-serif" fill="#0f172a" font-weight="600">${initials}</text></svg>`;
        const initialsDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(initialsSvg)}`;
        return {
          data: {
            id: node.id,
            label: name,
            profilePictureUrl: hasProfile ? node.profilePictureUrl : initialsDataUrl,
          },
        };
      }),
      ...socialGraph.links.map((link) => ({
        data: {
          id: `${link.source}-${link.target}`,
          source: link.source,
          target: link.target,
          weight: link.weight || 1,
        },
      })),
    ];

    if (socialGraph.nodes.length === 0) {
      return (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          No graph data yet
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Social Graph</h2>
            <p className="text-sm text-muted-foreground">Users connected by shared bills</p>
          </div>
          <Button variant="outline" onClick={() => loadSocialGraph(true)}>
            Refresh Graph
          </Button>
        </div>
        <div className="h-[calc(100vh-120px)] w-full rounded-lg border bg-background">
          <CytoscapeComponent
            elements={elements}
            style={{ width: '100%', height: '100%' }}
            layout={{
              name: 'cose-bilkent' as any,
              animate: false,
              idealEdgeLength: 520,
              nodeRepulsion: 3200000,
              gravity: 0.05,
              nodeDimensionsIncludeLabels: true,
              numIter: 5000,
            }}
            stylesheet={[
              {
                selector: 'node',
                style: {
                  width: 140,
                  height: 140,
                  label: 'data(label)',
                  'text-valign': 'bottom',
                  'text-halign': 'center',
                  'font-size': 72,
                  color: '#0f172a',
                  'text-margin-y': 40,
                  'background-color': '#e2e8f0',
                  'border-width': 2,
                  'border-color': '#94a3b8',
                  'background-fit': 'cover',
                  'background-image': 'data(profilePictureUrl)',
                },
              },
              {
                selector: 'edge',
                style: {
                  width: 'mapData(weight, 1, 8, 2, 7)',
                  'line-color': 'mapData(weight, 1, 8, #cbd5f5, #4338ca)' as any,
                  'curve-style': 'bezier',
                },
              },
              {
                selector: '.highlighted',
                style: {
                  'line-color': '#4f46e5',
                  opacity: 1,
                },
              },
              {
                selector: 'node.highlighted',
                style: {
                  'border-color': '#4f46e5',
                  'border-width': 3,
                },
              },
              {
                selector: 'node.dimmed',
                style: {
                  opacity: 0.3,
                },
              },
              {
                selector: 'edge.dimmed',
                style: {
                  opacity: 0.1,
                },
              },
            ]}
            cy={(cy: CytoscapeCore) => {
              if (!cy) return;
              cyRef.current = cy;
              cy.on('tap', 'node', (event: EventObject) => {
                const node = event.target;
                cy.elements().removeClass('highlighted dimmed');
                const connectedEdges = node.connectedEdges();
                const connectedNodes = connectedEdges.connectedNodes();
                connectedEdges.addClass('highlighted');
                node.addClass('highlighted');
                connectedNodes.addClass('highlighted');

                cy.elements().not(node).not(connectedEdges).not(connectedNodes).addClass('dimmed');
              });

              cy.on('tap', (event: EventObject) => {
                if (event.target === cy) {
                  cy.elements().removeClass('highlighted dimmed');
                }
              });
            }}
          />
        </div>
      </div>
    );
  };

  const renderRecurring = () => {
    const cadenceLabel = (c?: string) =>
      (
        ({
          daily: 'Daily',
          weekly: 'Weekly',
          biweekly: 'Every 2 weeks',
          monthly: 'Monthly',
        }) as Record<string, string>
      )[c || ''] ||
      c ||
      '-';
    const fmtDate = (iso?: string | null) =>
      iso
        ? new Date(iso).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })
        : '-';
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recurring Bills</CardTitle>
            <CardDescription>
              {recurringBills.length} templates
              {sweepResult ? ` · ${sweepResult}` : ''}
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={handleRunSweep} disabled={sweepRunning}>
              {sweepRunning ? 'Sweeping…' : 'Run sweep now'}
            </Button>
            <Label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={showTestData}
                onChange={(e) => setShowTestData(e.target.checked)}
                className="h-4 w-4 rounded border border-input accent-primary cursor-pointer"
              />
              Show test data
            </Label>
          </div>
        </CardHeader>
        <CardContent>
          {recurringBills.length === 0 ? (
            <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
              No recurring bills found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Creator</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Cadence</TableHead>
                  <TableHead>Next run</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recurringBills.map((rec) => {
                  const creatorUser = users.find((u) => u.id === rec.userId);
                  const creatorName =
                    rec.creator ||
                    (creatorUser
                      ? creatorUser.name ||
                        `${creatorUser.firstName || ''} ${creatorUser.lastName || ''}`.trim() ||
                        creatorUser.username
                      : '') ||
                    rec.userId.substring(0, 8) + '…';
                  const created = rec.occurrencesCreated || 0;
                  const symbol = rec.currency === 'INR' ? '₹' : '$';
                  const status = rec.scheduleStatus || 'active';
                  return (
                    <TableRow key={rec.id}>
                      <TableCell className="font-medium">
                        {rec.title}
                        {rec.isTestData && (
                          <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">
                            test
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="truncate max-w-[160px]">{creatorName}</span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {symbol}
                        {Number(rec.total || 0).toFixed(2)}
                        <span className="text-muted-foreground">
                          {' '}
                          · {rec.participants?.length || 0} people
                        </span>
                      </TableCell>
                      <TableCell>{cadenceLabel(rec.schedule?.cadence)}</TableCell>
                      <TableCell className="text-xs">
                        {status === 'active' ? fmtDate(rec.nextRunAt) : '-'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {created}
                        {rec.repeatCount ? ` of ${rec.repeatCount}` : ' (no limit)'}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            status === 'active'
                              ? 'text-emerald-700 border-emerald-300'
                              : status === 'paused'
                                ? 'text-amber-700 border-amber-300'
                                : 'text-muted-foreground'
                          }
                        >
                          {status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {status !== 'completed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRecurringStatus(rec)}
                            >
                              {status === 'paused' ? 'Resume' : 'Pause'}
                            </Button>
                          )}
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRecurringDelete(rec)}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderTrips = () => {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Trips</CardTitle>
            <CardDescription>{trips.length} total trips</CardDescription>
          </div>
          <Label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={showTestData}
              onChange={(e) => setShowTestData(e.target.checked)}
              className="h-4 w-4 rounded border border-input accent-primary cursor-pointer"
            />
            Show test data
          </Label>
        </CardHeader>
        <CardContent>
          {trips.length === 0 ? (
            <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
              No trips found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Share ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Creator</TableHead>
                  <TableHead>Participants</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Public link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trips.map((trip) => {
                  const members = trip.members || [];
                  const creatorMember = members.find((m) => m.id === trip.creatorId);
                  const creatorUser = users.find((u) => u.id === trip.creatorId);
                  const creatorName =
                    creatorMember?.name ||
                    (creatorUser
                      ? creatorUser.name ||
                        `${creatorUser.firstName || ''} ${creatorUser.lastName || ''}`.trim() ||
                        creatorUser.username
                      : '') ||
                    trip.creatorId.substring(0, 8) + '…';
                  const creatorAvatarUrl =
                    creatorMember?.profilePictureUrl ||
                    creatorMember?.venmoProfilePictureUrl ||
                    creatorUser?.profilePictureUrl ||
                    null;
                  const participantNames = members
                    .map((m) => m.name || (m.id ? m.id.substring(0, 6) : ''))
                    .filter(Boolean);
                  const tripUrl = trip.shareId ? getTripShareUrl(trip.shareId) : null;

                  return (
                    <TableRow key={trip.id}>
                      <TableCell className="font-mono text-xs">
                        {trip.shareId && tripUrl ? (
                          <a
                            href={tripUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary underline-offset-4 hover:underline"
                          >
                            {trip.shareId}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{trip.name || 'Untitled trip'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {creatorAvatarUrl ? (
                            <img
                              src={creatorAvatarUrl}
                              alt={creatorName}
                              className="h-6 w-6 rounded-full border border-border object-cover"
                            />
                          ) : (
                            <div className="h-6 w-6 rounded-full border border-border bg-muted" />
                          )}
                          <span className="truncate max-w-[160px]">{creatorName}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{members.length}</Badge>
                          <div className="flex -space-x-2">
                            {members.slice(0, FACE_PILE_LIMIT).map((m, i) => {
                              const avatar = m.profilePictureUrl || m.venmoProfilePictureUrl;
                              return avatar ? (
                                <img
                                  key={(m.id || '') + i}
                                  src={avatar}
                                  alt={m.name || ''}
                                  title={m.name || ''}
                                  className="h-6 w-6 rounded-full border border-background object-cover"
                                />
                              ) : (
                                <div
                                  key={(m.id || '') + i}
                                  title={m.name || ''}
                                  className="flex h-6 w-6 items-center justify-center rounded-full border border-background bg-muted text-[10px] text-muted-foreground"
                                >
                                  {(m.name || '?').charAt(0).toUpperCase()}
                                </div>
                              );
                            })}
                            {members.length > FACE_PILE_LIMIT && (
                              <div className="flex h-6 w-6 items-center justify-center rounded-full border border-background bg-muted text-[10px] text-muted-foreground">
                                +{members.length - FACE_PILE_LIMIT}
                              </div>
                            )}
                          </div>
                          <span className="truncate max-w-[220px] text-xs text-muted-foreground">
                            {participantNames.join(', ')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {trip.status ? (
                          <Badge variant="outline">{trip.status}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {trip.createdAt ? new Date(trip.createdAt).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {tripUrl ? (
                          <div className="flex flex-nowrap gap-2">
                            <Button
                              size="sm"
                              onClick={() => window.open(tripUrl, '_blank', 'noopener,noreferrer')}
                            >
                              Open
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigator.clipboard?.writeText(tripUrl)}
                            >
                              Copy link
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderNotifications = () => {
    // Substring filter across recipient, type, title, body, bill name, and status.
    const notificationQuery = notificationSearch.trim().toLowerCase();
    const filteredNotifications = notificationQuery
      ? notifications.filter((notification) =>
          [
            notification.id,
            notification.ticketId,
            notification.recipientName,
            notification.userId,
            notification.type,
            notification.title,
            notification.body,
            notification.billName,
            notification.status,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(notificationQuery)
        )
      : notifications;

    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Notifications</CardTitle>
            <CardDescription>
              {notificationUserFilter ? (
                <span className="inline-flex items-center gap-2">
                  Showing {notifications.length} for{' '}
                  <span className="font-semibold">{notificationUserFilter.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setNotificationUserFilter(null);
                      window.location.hash = 'notifications';
                    }}
                    className="rounded-full border px-2 text-xs hover:bg-muted"
                  >
                    Clear ×
                  </button>
                </span>
              ) : notificationQuery ? (
                `${filteredNotifications.length} of ${notifications.length} loaded`
              ) : (
                `${notifications.length} loaded${notifHasMore ? ' · more available' : ''}`
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={notificationSearch}
              onChange={(e) => setNotificationSearch(e.target.value)}
              aria-label="Search notifications"
              placeholder="Search by id, recipient, type, title, body..."
              className="h-9 w-72 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <Label className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={showTestData}
                onChange={(e) => setShowTestData(e.target.checked)}
                className="h-4 w-4 rounded border border-input accent-primary cursor-pointer"
              />
              Show test data
            </Label>
          </div>
        </CardHeader>
        <CardContent>
          {/* Server-side column filters — changing any reloads page 1. */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <select
              aria-label="Notification status"
              value={notifStatusFilter}
              onChange={(e) => setNotifStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">All statuses</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="suppressed">Suppressed</option>
            </select>
            <select
              aria-label="Notification delivery"
              value={notifDeliveryFilter}
              onChange={(e) => setNotifDeliveryFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">All delivery</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Not delivered</option>
              <option value="pending">Awaiting receipt</option>
              <option value="unknown">Unknown</option>
            </select>
            <select
              aria-label="Notification type"
              value={notifTypeFilter}
              onChange={(e) => setNotifTypeFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">All types</option>
              <option value="trip_pair_settled">Trip pair settled</option>
              <option value="payment_marked">Payment marked</option>
              <option value="payment_received">Payment received</option>
              <option value="batch_payment_received">Batch payment received</option>
              <option value="pending_confirmation">Pending confirmation</option>
              <option value="bill_created">Bill created</option>
              <option value="recurring_bill_created">Recurring bill created</option>
              <option value="collaborator_added">Collaborator added</option>
              <option value="trip_member_added">Trip member added</option>
              <option value="trip_bill_added">Trip bill added</option>
            </select>
            {(notifStatusFilter || notifDeliveryFilter || notifTypeFilter) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setNotifStatusFilter('');
                  setNotifDeliveryFilter('');
                  setNotifTypeFilter('');
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
          {notificationsTableMissing && (
            <div className="mb-4 rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
              Notifications table not provisioned in this environment yet.
            </div>
          )}
          {filteredNotifications.length === 0 ? (
            <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
              {notificationQuery
                ? `No notifications match "${notificationSearch.trim()}"`
                : 'No notifications found'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recorded (UTC)</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Title / Body</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Context</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNotifications.map((notification) => (
                  <React.Fragment key={notification.id}>
                    <TableRow
                      className="cursor-pointer"
                      onClick={() =>
                        setExpandedNotificationId((cur) =>
                          cur === notification.id ? null : notification.id
                        )
                      }
                    >
                      <TableCell className="whitespace-nowrap text-xs">
                        {dateTime(notification.createdAt)}
                        <div
                          className="font-mono text-[10px] text-muted-foreground"
                          title={notification.id}
                        >
                          {notification.id.slice(0, 8)}…
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="truncate max-w-[160px]">
                          <button
                            className="admin-text-link"
                            onClick={() => onUser(notification.userId)}
                          >
                            {notification.recipientName || 'Unknown'}
                          </button>
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {notification.userId}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{notification.type}</TableCell>
                      <TableCell className="max-w-[280px]">
                        <div className="font-semibold">{notification.title}</div>
                        <div className="text-xs text-muted-foreground">{notification.body}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <Badge className={getNotificationStatusBadgeClass(notification.status)}>
                            {notification.status}
                          </Badge>
                          {notification.status === 'sent' &&
                            (notification.deliveryStatus === 'delivered' ? (
                              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                delivered
                              </Badge>
                            ) : notification.deliveryStatus === 'failed' ? (
                              <Badge
                                className="bg-red-50 text-red-700 border-red-200"
                                title={notification.deliveryError}
                              >
                                not delivered
                              </Badge>
                            ) : notification.deliveryStatus === 'unknown' ? (
                              <Badge className="bg-muted text-muted-foreground border-border">
                                unknown
                              </Badge>
                            ) : (
                              <Badge className="bg-muted text-muted-foreground border-border">
                                awaiting receipt
                              </Badge>
                            ))}
                          {notification.deliveryError && (
                            <span
                              className="text-[11px] text-red-600 max-w-[140px] truncate"
                              title={notification.deliveryError}
                            >
                              {notification.deliveryError}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {notification.billName || notification.billId ? (
                          <div className="truncate max-w-[200px]">
                            <button
                              className="admin-text-link"
                              onClick={(event) => {
                                event.stopPropagation();
                                onBill(notification.billId!);
                              }}
                            >
                              {notification.billName || notification.billId}
                            </button>
                          </div>
                        ) : null}
                        {notification.tripId && (
                          <div className="font-mono text-muted-foreground truncate max-w-[200px]">
                            {notification.tripId}
                          </div>
                        )}
                        {notification.pairKey && (
                          <div className="font-mono text-muted-foreground truncate max-w-[200px]">
                            {notification.pairKey}
                          </div>
                        )}
                        {notification.error && (
                          <div className="text-red-600 truncate max-w-[200px]">
                            {notification.error}
                          </div>
                        )}
                        {!notification.billName &&
                          !notification.billId &&
                          !notification.tripId &&
                          !notification.pairKey &&
                          !notification.error && <span className="text-muted-foreground">-</span>}
                      </TableCell>
                    </TableRow>
                    {expandedNotificationId === notification.id && (
                      <TableRow>
                        <TableCell colSpan={6} className="bg-muted/40">
                          {/* Full, untruncated record — ids are copyable from here. */}
                          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 py-1 text-xs md:grid-cols-3">
                            <div>
                              <span className="text-muted-foreground">Notification ID</span>
                              <div className="select-all break-all font-mono">
                                {notification.id}
                              </div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Recipient</span>
                              <div>{notification.recipientName || 'Unknown'}</div>
                              <div className="select-all break-all font-mono text-muted-foreground">
                                {notification.userId}
                              </div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Sent at</span>
                              <div className="font-mono">{notification.createdAt}</div>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Status</span>
                              <div>
                                {notification.status}
                                {notification.status === 'sent' &&
                                  ` · ${notification.deliveryStatus || 'awaiting receipt'}`}
                                {notification.isTestData ? ' · test data' : ''}
                              </div>
                            </div>
                            {notification.ticketId && (
                              <div>
                                <span className="text-muted-foreground">Expo ticket</span>
                                <div className="select-all break-all font-mono">
                                  {notification.ticketId}
                                </div>
                              </div>
                            )}
                            {(notification.error || notification.deliveryError) && (
                              <div>
                                <span className="text-muted-foreground">Error</span>
                                <div className="break-words text-red-600">
                                  {notification.error || notification.deliveryError}
                                </div>
                              </div>
                            )}
                            <div className="col-span-2 md:col-span-3">
                              <span className="text-muted-foreground">Title / Body</span>
                              <div className="font-semibold">{notification.title}</div>
                              <div className="break-words">{notification.body}</div>
                            </div>
                            {(notification.billId || notification.billName) && (
                              <div>
                                <span className="text-muted-foreground">Bill</span>
                                <div className="break-words">{notification.billName}</div>
                                <div className="select-all break-all font-mono text-muted-foreground">
                                  {notification.billId}
                                </div>
                              </div>
                            )}
                            {notification.tripId && (
                              <div>
                                <span className="text-muted-foreground">Trip</span>
                                <div className="select-all break-all font-mono">
                                  {notification.tripId}
                                </div>
                              </div>
                            )}
                            {notification.pairKey && (
                              <div>
                                <span className="text-muted-foreground">Pair key</span>
                                <div className="select-all break-all font-mono">
                                  {notification.pairKey}
                                </div>
                              </div>
                            )}
                            {typeof notification.amount === 'number' && (
                              <div>
                                <span className="text-muted-foreground">Amount</span>
                                <div>{notification.amount}</div>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
          {notifHasMore && (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={loadMoreNotifications} disabled={notifLoadingMore}>
                {notifLoadingMore ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const getUserDisplayName = (u: User) =>
    u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username || u.id;

  const getUserInitials = (u: User) => {
    if (u.firstName && u.lastName) return `${u.firstName.charAt(0)}${u.lastName.charAt(0)}`;
    if (u.name)
      return u.name
        .split(' ')
        .map((w) => w.charAt(0))
        .slice(0, 2)
        .join('');
    return (u.username || u.venmoUsername || u.id).charAt(0).toUpperCase();
  };

  const flagSearchResults = (() => {
    const q = flagUserSearch.trim().toLowerCase();
    if (q.length < 1) return [];
    return users
      .filter((u) => u.userType !== 'known')
      .filter((u) => !flagForm.allowedUsers.includes(u.id))
      .filter((u) => {
        const haystack = [
          u.firstName,
          u.lastName,
          u.name,
          u.username,
          u.venmoUsername,
          u.phoneNumber,
          u.id,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      })
      .slice(0, 8);
  })();

  const renderFeatures = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Feature Flags</CardTitle>
            <CardDescription>Manage feature flags for gated rollouts</CardDescription>
          </div>
          <Button
            onClick={() => {
              setEditingFlag(null);
              setFlagForm({
                key: '',
                name: '',
                description: '',
                enabledForAll: false,
                allowedUsers: [],
              });
              setFlagUserSearch('');
              setFeatureFlagModalOpen(true);
            }}
          >
            Create Flag
          </Button>
        </CardHeader>
        <CardContent>
          {featureFlags.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No feature flags yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Allowed Users</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {featureFlags
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((flag) => (
                    <TableRow key={flag.flagId}>
                      <TableCell className="font-mono text-sm">{flag.key}</TableCell>
                      <TableCell>{flag.name}</TableCell>
                      <TableCell>
                        {flag.enabledForAll ? (
                          <Badge variant="default">Enabled</Badge>
                        ) : (flag.allowedUsers?.length || 0) > 0 ? (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100">
                            Dogfooding
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Disabled</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {flag.enabledForAll ? (
                          <span className="text-xs text-muted-foreground">All users</span>
                        ) : (flag.allowedUsers?.length || 0) > 0 ? (
                          <div className="flex -space-x-1.5">
                            {flag.allowedUsers.slice(0, FACE_PILE_LIMIT).map((uid) => {
                              const u = users.find((u) => u.id === uid);
                              return u?.profilePictureUrl ? (
                                <img
                                  key={uid}
                                  src={u.profilePictureUrl}
                                  alt={getUserDisplayName(u)}
                                  title={getUserDisplayName(u)}
                                  className="h-6 w-6 rounded-full border-2 border-white object-cover"
                                />
                              ) : (
                                <div
                                  key={uid}
                                  title={u ? getUserDisplayName(u) : uid}
                                  className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-indigo-500 to-purple-500 text-[10px] font-semibold text-white"
                                >
                                  {u ? getUserInitials(u) : '?'}
                                </div>
                              );
                            })}
                            {flag.allowedUsers.length > FACE_PILE_LIMIT && (
                              <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-muted text-[10px] font-medium text-muted-foreground">
                                +{flag.allowedUsers.length - FACE_PILE_LIMIT}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(flag.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingFlag(flag);
                              setFlagForm({
                                key: flag.key,
                                name: flag.name,
                                description: flag.description || '',
                                enabledForAll: flag.enabledForAll,
                                allowedUsers: [...(flag.allowedUsers || [])],
                              });
                              setFlagUserSearch('');
                              setFeatureFlagModalOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteFlag(flag.flagId, flag.key)}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={featureFlagModalOpen}
        onOpenChange={(open) => {
          setFeatureFlagModalOpen(open);
          if (!open) setFlagUserSearch('');
        }}
      >
        <DialogContent className="vs-design admin-ui admin-dialog max-w-lg overflow-visible">
          <DialogHeader>
            <DialogTitle>{editingFlag ? 'Edit Feature Flag' : 'Create Feature Flag'}</DialogTitle>
            <DialogDescription>
              {editingFlag
                ? 'Update the feature flag configuration.'
                : 'Create a new feature flag for gated rollout.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Key</Label>
              <Input
                placeholder="e.g. new_tab_ui"
                value={flagForm.key}
                onChange={(e) => setFlagForm((prev) => ({ ...prev, key: e.target.value }))}
                disabled={!!editingFlag}
              />
            </div>
            <div>
              <Label>Name</Label>
              <Input
                placeholder="Human-readable name"
                value={flagForm.name}
                onChange={(e) => setFlagForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="Optional description"
                value={flagForm.description}
                onChange={(e) => setFlagForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enabledForAll"
                checked={flagForm.enabledForAll}
                onChange={(e) =>
                  setFlagForm((prev) => ({ ...prev, enabledForAll: e.target.checked }))
                }
              />
              <Label htmlFor="enabledForAll">Enabled for All Users</Label>
            </div>
            {!flagForm.enabledForAll && (
              <div>
                <Label>Allowed Users</Label>
                {/* Selected users as pills */}
                {flagForm.allowedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 mb-2">
                    {flagForm.allowedUsers.map((uid) => {
                      const u = users.find((u) => u.id === uid);
                      return (
                        <span
                          key={uid}
                          className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 border border-indigo-200 pl-1 pr-2 py-0.5 text-sm"
                        >
                          {u?.profilePictureUrl ? (
                            <img
                              src={u.profilePictureUrl}
                              alt=""
                              className="h-5 w-5 rounded-full object-cover"
                            />
                          ) : (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-[9px] font-bold text-white">
                              {u ? getUserInitials(u) : '?'}
                            </span>
                          )}
                          <span className="text-indigo-800 font-medium">
                            {u ? getUserDisplayName(u) : uid}
                          </span>
                          <button
                            type="button"
                            className="ml-0.5 text-indigo-400 hover:text-indigo-700 transition-colors"
                            onClick={() =>
                              setFlagForm((prev) => ({
                                ...prev,
                                allowedUsers: prev.allowedUsers.filter((id) => id !== uid),
                              }))
                            }
                          >
                            <svg
                              className="h-3.5 w-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                {/* Search input */}
                <div className="relative">
                  <Input
                    placeholder="Search users by name, username, or phone..."
                    value={flagUserSearch}
                    onChange={(e) => setFlagUserSearch(e.target.value)}
                  />
                  {/* Dropdown results */}
                  {flagUserSearch.trim().length >= 1 && flagSearchResults.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg max-h-52 overflow-y-auto">
                      {flagSearchResults.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors first:rounded-t-lg last:rounded-b-lg"
                          onClick={() => {
                            setFlagForm((prev) => ({
                              ...prev,
                              allowedUsers: [...prev.allowedUsers, u.id],
                            }));
                            setFlagUserSearch('');
                          }}
                        >
                          {u.profilePictureUrl ? (
                            <img
                              src={u.profilePictureUrl}
                              alt={getUserDisplayName(u)}
                              className="h-7 w-7 rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-[10px] font-semibold text-white">
                              {getUserInitials(u)}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-900 truncate">
                              {getUserDisplayName(u)}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              {u.username ? `@${u.username}` : ''}
                              {u.username && u.phoneNumber ? ' · ' : ''}
                              {u.phoneNumber || ''}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {flagUserSearch.trim().length >= 1 && flagSearchResults.length === 0 && (
                    <div className="absolute z-50 mt-1 w-full rounded-lg border bg-white shadow-lg px-3 py-3 text-sm text-muted-foreground text-center">
                      No matching users
                    </div>
                  )}
                </div>
              </div>
            )}
            <Button
              className="w-full"
              onClick={editingFlag ? handleUpdateFlag : handleCreateFlag}
              disabled={!flagForm.key}
            >
              {editingFlag ? 'Update Flag' : 'Create Flag'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dangerous action confirmation dialog */}
      <Dialog
        open={!!flagConfirmDialog}
        onOpenChange={(open) => {
          if (!open) setFlagConfirmDialog(null);
        }}
      >
        <DialogContent className="vs-design admin-ui admin-dialog max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">
              {flagConfirmDialog?.type === 'delete'
                ? 'Delete Feature Flag'
                : 'Enable for All Users'}
            </DialogTitle>
            <DialogDescription>
              {flagConfirmDialog?.type === 'delete' ? (
                <>
                  Are you sure you want to delete{' '}
                  <span className="font-mono font-semibold">{flagConfirmDialog?.flagKey}</span>?
                  This will immediately remove the flag for all users and cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to enable{' '}
                  <span className="font-mono font-semibold">{flagConfirmDialog?.flagKey}</span> for{' '}
                  <span className="font-semibold">all users</span>? This will immediately roll out
                  the feature to everyone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setFlagConfirmDialog(null)}>
              Cancel
            </Button>
            <Button
              className={
                flagConfirmDialog?.type === 'delete'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-amber-600 hover:bg-amber-700'
              }
              onClick={async () => {
                if (flagConfirmDialog?.type === 'delete') {
                  await executeDeleteFlag();
                } else {
                  setFlagConfirmDialog(null);
                  await executeUpdateFlag();
                }
              }}
            >
              {flagConfirmDialog?.type === 'delete' ? 'Delete Flag' : 'Enable for Everyone'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  const renderSandbox = () => (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-4">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Receipt playground</CardTitle>
            <CardDescription className="text-xs">
              Upload up to 5 receipt photos and run parser.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input type="file" accept="image/*" multiple onChange={handleSandboxFilesChanged} />
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Claude model</Label>
              <Select value={sandboxModel} onChange={(e) => setSandboxModel(e.target.value)}>
                <option value="claude-sonnet-4-6">Sonnet 4.6</option>
                <option value="claude-sonnet-5">Sonnet 4.5</option>
                <option value="claude-sonnet-4-20250514">Sonnet 4</option>
                <option value="claude-3-5-sonnet-20241022">Sonnet 3.5</option>
                <option value="claude-opus-4-1-20250805">Opus 4.1</option>
                <option value="claude-opus-4-20250514">Opus 4</option>
              </Select>
            </div>
            {sandboxFiles.length > 0 && (
              <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                {sandboxFiles.length} file{sandboxFiles.length === 1 ? '' : 's'} selected
              </div>
            )}
            <div className="flex items-center gap-3">
              <Button
                onClick={handleSandboxParse}
                disabled={sandboxFiles.length === 0 || sandboxParsing}
              >
                {sandboxParsing ? 'Parsing…' : 'Run Parser'}
              </Button>
              {sandboxResult && (
                <span className="text-xs text-muted-foreground">
                  {sandboxResult.imageCount} image{sandboxResult.imageCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {sandboxError && <div className="text-xs text-destructive">{sandboxError}</div>}
            {sandboxErrorDetails && (
              <pre className="max-h-40 overflow-auto rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
                {sandboxErrorDetails}
              </pre>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Images Sent to Claude</CardTitle>
            <CardDescription className="text-xs">
              Normalized/compressed images used for vision parsing.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {sandboxResult?.sandboxDetails?.processedImages &&
            sandboxResult.sandboxDetails.processedImages.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {sandboxResult.sandboxDetails.processedImages.map((image) => (
                  <div key={`${image.index}-${image.sourceUrl}`} className="rounded-md border p-2">
                    <div className="mb-1 text-xs text-muted-foreground">
                      Image {image.index + 1} | {image.mediaType}
                    </div>
                    {image.dataUrl && (
                      <button
                        type="button"
                        className="w-full"
                        onClick={() =>
                          setSandboxPreviewImage({
                            src: image.dataUrl as string,
                            title: `Processed receipt ${image.index + 1}`,
                          })
                        }
                      >
                        <img
                          src={image.dataUrl}
                          alt={`Processed receipt ${image.index + 1}`}
                          className="max-h-44 w-full rounded border object-contain bg-muted/20"
                        />
                      </button>
                    )}
                    <div className="mt-1 text-xs text-muted-foreground">
                      Original: {formatBytes(image.originalBytes)} | Processed:{' '}
                      {formatBytes(image.processedBytes)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Compressed: {image.wasCompressed ? 'yes' : 'no'} | HEIC converted:{' '}
                      {image.convertedFromHeic ? 'yes' : 'no'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Run parser to view processed images.
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Claude Prompt</CardTitle>
          </CardHeader>
          <CardContent>
            <details className="rounded-md border bg-muted/20 p-2 text-xs" open={false}>
              <summary className="cursor-pointer select-none font-medium text-foreground">
                Show prompt
              </summary>
              <pre className="mt-2 max-h-[340px] overflow-auto whitespace-pre-wrap">
                {(sandboxResult?.sandboxDetails?.prompts &&
                  sandboxResult.sandboxDetails.prompts[0]) ||
                  'No prompt captured.'}
              </pre>
            </details>
          </CardContent>
        </Card>
      </div>

      {sandboxResult?.parsedBill && (
        <div className="grid gap-4 lg:grid-cols-2">
          {(() => {
            const sandboxCurrency = normalizeCurrency(sandboxResult.parsedBill.currency || 'USD');
            return (
              <>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Claude Response</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-3 rounded-md border p-2 text-xs text-muted-foreground">
                      Claude confidence:{' '}
                      {formatClaudeConfidence(sandboxResult.receiptData?.confidence)} | Bill
                      completeness score:{' '}
                      {formatBillCompletenessScore(getBillCompletenessScore(sandboxResult))} | Why
                      not 100%: {getConfidenceExplanation(sandboxResult)}
                    </div>
                    {sandboxResult.sandboxDetails?.callMetrics?.[0] && (
                      <div className="mb-3 rounded-md border p-2 text-xs text-muted-foreground">
                        Model:{' '}
                        {sandboxResult.model ||
                          sandboxResult.sandboxDetails.callMetrics[0].model ||
                          'unknown'}{' '}
                        | Input tokens:{' '}
                        {sandboxResult.sandboxDetails.callMetrics[0].usage?.input_tokens ?? 'N/A'} |
                        Output tokens:{' '}
                        {sandboxResult.sandboxDetails.callMetrics[0].usage?.output_tokens ?? 'N/A'}
                        {sandboxResult.claudeDurationMs !== undefined &&
                          sandboxResult.claudeDurationMs !== null && (
                            <>
                              {' '}
                              | Claude time: {(sandboxResult.claudeDurationMs / 1000).toFixed(2)}s
                            </>
                          )}
                        {sandboxResult.sandboxDetails.callMetrics[0].estimatedCostUsd !==
                          undefined &&
                          sandboxResult.sandboxDetails.callMetrics[0].estimatedCostUsd !== null && (
                            <>
                              {' '}
                              | Estimated cost:{' '}
                              {(
                                sandboxResult.sandboxDetails.callMetrics[0].estimatedCostUsd * 100
                              ).toFixed(3)}{' '}
                              cents
                            </>
                          )}
                      </div>
                    )}
                    <pre className="max-h-[560px] overflow-auto rounded-md border bg-muted/20 p-3 text-xs whitespace-pre-wrap">
                      {(sandboxResult?.sandboxDetails?.rawResponses &&
                        sandboxResult.sandboxDetails.rawResponses[0]) ||
                        'No raw response captured.'}
                    </pre>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Final Bill Information</CardTitle>
                    <CardDescription className="text-xs">
                      {sandboxResult.parsedBill.title || 'Untitled Bill'} | Claude confidence:{' '}
                      {formatClaudeConfidence(sandboxResult.receiptData?.confidence)} | Bill
                      completeness score:{' '}
                      {formatBillCompletenessScore(getBillCompletenessScore(sandboxResult))} |
                      Items: {sandboxResult.parsedBill.items.length}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="rounded-md border p-2 text-xs text-muted-foreground">
                      Why not 100%: {getConfidenceExplanation(sandboxResult)}
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item</TableHead>
                            <TableHead>Qty</TableHead>
                            <TableHead>Base Price</TableHead>
                            <TableHead>Total Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sandboxResult.parsedBill.items.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <div className="font-medium">{item.name}</div>
                                {item.addons && item.addons.length > 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    Addons:{' '}
                                    {item.addons
                                      .map(
                                        (a) =>
                                          `${a.name} (+${formatBillAmount(Number(a.price || 0), sandboxCurrency)})`
                                      )
                                      .join(', ')}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>{item.quantity || 1}</TableCell>
                              <TableCell>
                                {formatBillAmount(Number(item.price || 0), sandboxCurrency)}
                              </TableCell>
                              <TableCell>
                                {formatBillAmount(Number(item.amount || 0), sandboxCurrency)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="grid gap-2 text-xs md:grid-cols-4">
                      <div className="rounded-md border p-2">
                        Subtotal:{' '}
                        {formatBillAmount(
                          Number(sandboxResult.parsedBill.subtotal || 0),
                          sandboxCurrency
                        )}
                      </div>
                      <div className="rounded-md border p-2">
                        Tax:{' '}
                        {formatBillAmount(
                          Number(sandboxResult.parsedBill.tax || 0),
                          sandboxCurrency
                        )}
                      </div>
                      <div className="rounded-md border p-2">
                        Tip:{' '}
                        {formatBillAmount(
                          Number(sandboxResult.parsedBill.tip || 0),
                          sandboxCurrency
                        )}
                      </div>
                      <div className="rounded-md border p-2 font-semibold">
                        Total:{' '}
                        {formatBillAmount(
                          Number(sandboxResult.parsedBill.total || 0),
                          sandboxCurrency
                        )}
                      </div>
                    </div>

                    <div className="grid gap-2 md:grid-cols-2">
                      {Object.keys(sandboxResult.parsedBill.surcharges || {}).length > 0 && (
                        <div className="rounded-md border p-2 text-xs">
                          <div className="font-medium">Surcharges</div>
                          <div className="mt-1 text-muted-foreground">
                            {Object.entries(sandboxResult.parsedBill.surcharges).map(
                              ([key, value]) => (
                                <div key={key}>
                                  {key}: {formatBillAmount(Number(value || 0), sandboxCurrency)}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {Object.keys(sandboxResult.parsedBill.discounts || {}).length > 0 && (
                        <div className="rounded-md border p-2 text-xs">
                          <div className="font-medium">Discounts</div>
                          <div className="mt-1 text-muted-foreground">
                            {Object.entries(sandboxResult.parsedBill.discounts).map(
                              ([key, value]) => (
                                <div key={key}>
                                  {key}: {formatBillAmount(Number(value || 0), sandboxCurrency)}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </>
            );
          })()}
        </div>
      )}

      {!sandboxResult?.parsedBill && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Claude Response</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="max-h-[340px] overflow-auto rounded-md border bg-muted/20 p-3 text-xs whitespace-pre-wrap">
              {(sandboxResult?.sandboxDetails?.rawResponses &&
                sandboxResult.sandboxDetails.rawResponses[0]) ||
                'No raw response captured.'}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderSandboxImageModal = () => (
    <Dialog
      open={!!sandboxPreviewImage}
      onOpenChange={(open) => !open && setSandboxPreviewImage(null)}
    >
      <DialogContent className="vs-design admin-ui admin-dialog max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{sandboxPreviewImage?.title || 'Processed image'}</DialogTitle>
        </DialogHeader>
        {sandboxPreviewImage?.src && (
          <img
            src={sandboxPreviewImage.src}
            alt={sandboxPreviewImage.title || 'Processed image preview'}
            className="w-full rounded border object-contain bg-muted/20 max-h-[75vh]"
          />
        )}
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setSandboxPreviewImage(null)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  const renderers: Record<LegacySection, () => React.ReactNode> = {
    trips: renderTrips,
    recurring: renderRecurring,
    notifications: renderNotifications,
    social: renderSocialGraph,
    sandbox: renderSandbox,
    features: renderFeatures,
    feedback: () => <FeedbackInbox refreshKey={feedbackRefreshKey} />,
  };
  return (
    <div className="admin-legacy">
      {panelError && (
        <div className="admin-notice is-error" role="alert">
          {panelError}
        </div>
      )}
      {panelError ? null : tabLoading[activeTab] ? (
        <div className="admin-loading" role="status">
          Loading section…
        </div>
      ) : (
        renderers[activeTab]()
      )}
      {renderSandboxImageModal()}
    </div>
  );
};
export default AdminLegacyPanels;
